import { ElementType, memo, useContext, useEffect, useState } from 'react';

import {
  DraggableAttributes,
  DraggableSyntheticListeners,
  useDraggable
} from '@dnd-kit/core';
import { PlanningEntityType } from 'generated';
import { useRecoilState, useRecoilValue } from 'recoil';

import { Event } from '../../types';
import { EditAppointmentType } from 'RestAPIs/activities/types';

import {
  GRAY_LIGHT_SECONDARY,
  ORANGE,
  PRIMARY,
  RED,
  WHITE
} from 'constants/colors';
import { CARD_WIDTH_FULL_WIDTH } from 'constants/widths';

import {
  getActivityTypeColor,
  getAddressNotFound,
  getHasWarnings
} from '../../utils';
import { brightness } from 'utils/misc';

import {
  assignEmploymentEditAppointmentTypeAtom,
  dragActionActiveAtom,
  selectedContextMenuOptionAtom,
  shouldShowPopoverDialogAtom
} from '../../atoms';

import { Box, styled, SxProps, Tooltip } from '@mui/material';
import { EventTooltip } from '../EventTooltip';
import ContextMenuContainer from '../ContextMenu/ContextMenuContainer';
import PopoverWarningDialog from '../PopoverWarningDialog';

import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';
import { ZoomLevel } from '../Header/ZoomLevelMenu';
import { getZoomLevelProps } from './activity-zoom-level-props';
import { useNavParams, useSelector } from 'hooks';
import { View } from '../../enums';
import { AddressSection, NameSection, TimeSection } from './components';
import { SignalRow } from '../SignalRow';
import { CopyModeReturn } from '../../hooks/useCopyMode';
import SecondNameSection from './components/SecondNameSection';
import dayjs from 'dayjs';
import {
  StyledBoxFlexColumnNoGap,
  StyledBoxFlexStartSmallGap
} from 'components/shared/Box/Box.styles';

type CardProps = Omit<
  Event,
  | 'droppable'
  | 'category'
  | 'date'
  | 'end'
  | 'status'
  | 'isSubmittable'
  | 'isApprovable'
  | 'isDeletable'
> & {
  planningEntityId: string;
  planningEntityType: PlanningEntityType;
};

interface ActivityCardProps extends CardProps {
  component?: ElementType<any>;
  copyMode?: CopyModeReturn;
  dragActionActive?: boolean;
  setDragDisabled?: (disabled: boolean) => void;
  selected: boolean;
  listeners?: DraggableSyntheticListeners;
  attributes?: DraggableAttributes;
  hovered?: boolean;
  setHovered?: (hovered: boolean) => void;
  isTaskList?: boolean;
  sx?: SxProps;
}

export const ActivityCard = memo((props: ActivityCardProps) => {
  const {
    appointmentGrouping,
    id,
    organizationalUnit,
    activity,
    workPlace,
    project,
    employeeType,
    planningEntityType,
    clients,
    copyMode,
    employees,
    component = 'div',
    noteInternal,
    dragActionActive,
    durationInSeconds,
    warnings,
    isAbsent,
    isAssigned,
    isEditable,
    isCreditable,
    isCredit,
    isOpen,
    isRecurring,
    setDragDisabled,
    scheduleStart,
    start,
    selected,
    timeWindow,
    location,
    schedulingLabel,
    sx,
    listeners,
    attributes,
    hovered,
    linkedDutyCode,
    isTaskList = false,
    setHovered
  } = props;
  const {
    startsTooEarly,
    startsTooLate,
    isInSync: isInSyncWithEcd,
    hasOverlap,
    isOverQualified: overQualified,
    isUnderQualified: underQualified,
    excludedEmployeeClientCombination
  } = warnings || {};
  const client = clients?.[0];
  const employee = employees?.[0];

  const address = client?.address
    ? `${client.address.streetName} ${client.address.houseNumber}, ${client.address.postalCode} ${client.address.city}`
    : '';

  const addressNotFound = getAddressNotFound(location, activity);
  const cardTypeColor = getActivityTypeColor(!!client, isAbsent);
  const { isCopyMode, copyId, pasteAppointment } = copyMode ?? {};

  const { params } = useNavParams();
  const { defaultZoomLevel } = useSelector((state) => state.preferences);
  const zoomLevel = defaultZoomLevel ?? ZoomLevel.STANDARD;

  const [contextMenuActive, setContextMenuActive] = useState<boolean>(false);
  const allowToolTip: boolean =
    !!hovered && !contextMenuActive && !dragActionActive && !isCopyMode;

  const allowContextMenu = !isCopyMode;

  const enableCardZoomLevels: boolean =
    params?.view === View.Cards ||
    params?.view === View.Roster ||
    params?.view === View.Timeline;

  const { showAddress, showSignals, signalThreshold, titleLineClamp } =
    getZoomLevelProps(enableCardZoomLevels, zoomLevel);

  const hasWarnings = getHasWarnings({
    startsTooEarly,
    startsTooLate,
    isInSyncWithEcd,
    excludedEmployeeClientCombination,
    isUrgent: !!timeWindow?.isUrgent,
    hasOverlap
  });

  return (
    <Tooltip
      enterNextDelay={500}
      disableHoverListener={!allowToolTip}
      sx={{
        zIndex: 0,
        boxSizing: 'border-box',
        flexShrink: 0,
        borderColor: GRAY_LIGHT_SECONDARY,
        border:
          isCopyMode && id == copyId
            ? `2px dashed ${PRIMARY}`
            : '1px solid #EFEFEF',
        bgcolor: selected
          ? brightness(cardTypeColor, 90)
          : workPlace && isOpen
          ? '#fdefd6'
          : 'lightGray.main',
        overflow: 'hidden',
        borderLeft: isRecurring
          ? `4px solid ${cardTypeColor}`
          : `2px solid ${cardTypeColor}`,
        position: 'relative',
        cursor: isCopyMode && (project || workPlace) ? 'pointer' : 'auto',
        minHeight: zoomLevel !== ZoomLevel.COMPACT ? 50 : 'initial',
        opacity: selected && dragActionActive ? 0.5 : 1,
        ...(sx ? sx : {})
      }}
      onClick={() => {
        if (isCopyMode && (project?.entityId || workPlace?.entityId)) {
          pasteAppointment?.({
            organizationalUnitId: organizationalUnit?.id,
            projectEntityId: project?.entityId,
            workPlaceEntityId: workPlace?.entityId,
            activityEntityId: activity ? activity?.entityId : undefined,
            startDate: start
          });
        }
      }}
      title={
        allowToolTip && (
          <EventTooltip
            event={props as unknown as Event}
            onMouseOver={() => {
              setHovered?.(false);
            }}
          />
        )
      }
    >
      <Box
        {...listeners}
        {...attributes}
        id={id}
        component={component}
        className={`${isAbsent ? 'absent-card ' : ''} __${id}__ __${dayjs(
          start
        ).format('YYYY-MM-DD')}__ activity-card `}
        sx={{
          boxSizing: 'border-box',
          flexShrink: 0,
          ...(isCopyMode &&
            id == copyId && { border: `2px dashed ${PRIMARY}` }),
          bgcolor: selected ? brightness(cardTypeColor, 90) : WHITE,
          overflow: 'hidden',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          borderLeft: isRecurring
            ? `4px solid ${cardTypeColor}`
            : `2px solid ${cardTypeColor}`,
          position: 'relative',
          cursor: (project || workPlace) && isCopyMode ? 'pointer' : 'auto',
          minHeight: zoomLevel !== ZoomLevel.COMPACT ? 50 : 'initial',
          opacity: selected && dragActionActive ? 0.5 : 1,
          marginBottom: isTaskList && isAbsent ? '8px' : 'initial',
          '&:hover': {
            ...((project || workPlace) &&
              isCopyMode &&
              id !== copyId && { outline: `2px dashed ${ORANGE}` })
          },
          ...(hasWarnings && { backgroundColor: brightness(RED, 90) }),
          ...(sx ? sx : {})
        }}
        onClick={() => {
          if (isCopyMode && (project?.entityId || workPlace?.entityId)) {
            pasteAppointment?.({
              organizationalUnitId: organizationalUnit?.id,
              ...(project?.entityId
                ? { projectEntityId: project?.entityId }
                : {}),
              ...(workPlace?.entityId
                ? { workPlaceEntityId: workPlace?.entityId }
                : {}),
              activityEntityId: activity ? activity?.entityId : undefined,
              startDate: start
            });
          }
        }}
      >
        <StyledBoxFlexStartSmallGap>
          <StyledBoxFlexColumnNoGap>
            <TimeSection
              durationInSeconds={durationInSeconds}
              isAbsent={isAbsent}
              project={project}
              start={start}
            />

            {showSignals && (
              <SignalRow
                schedulingLabel={schedulingLabel}
                addressNotFound={addressNotFound}
                noteInternal={noteInternal}
                excludedEmployeeClientCombination={
                  excludedEmployeeClientCombination
                }
                hasOverlap={hasOverlap}
                isAbsent={isAbsent}
                isInSyncWithEcd={isInSyncWithEcd}
                isRecurring={isRecurring}
                locationType={location?.locationType}
                overQualified={overQualified}
                selected={selected}
                signalThreshold={signalThreshold}
                startsTooEarly={startsTooEarly}
                startsTooLate={startsTooLate}
                timeWindow={timeWindow}
                underQualified={underQualified}
              />
            )}
          </StyledBoxFlexColumnNoGap>

          <StyledBoxFlexStartSmallGap>
            <StyledBoxFlexColumnNoGap>
              <NameSection
                planningEntityType={planningEntityType}
                activity={activity}
                client={client}
                isAbsent={isAbsent}
                titleLineClamp={titleLineClamp}
              />
              <SecondNameSection
                planningEntityType={planningEntityType}
                schedulingLabel={schedulingLabel}
                activity={activity}
                employee={employee}
                client={client}
                employeeType={employeeType}
                isAbsent={isAbsent}
                linkedDutyCode={linkedDutyCode}
              />
            </StyledBoxFlexColumnNoGap>
            {allowContextMenu && (
              <ContextMenuContainer
                eventId={id}
                scheduleStart={scheduleStart}
                clientId={client?.id}
                eventIsOpen={isOpen}
                hasAppointmentGrouping={!!appointmentGrouping}
                hasEmployee={!!employee}
                isAssigned={!!isAssigned}
                isAbsent={isAbsent}
                isEditable={isEditable}
                isCreditable={isCreditable}
                isCredit={isCredit}
                schedulingLabel={schedulingLabel}
                isProject={!!project}
                isWorkPlace={!!workPlace}
                onOpen={(open: boolean) => {
                  setContextMenuActive(open);
                  setDragDisabled?.(open);
                }}
                sx={{ position: 'absolute', top: 0, right: 0 }}
              />
            )}
          </StyledBoxFlexStartSmallGap>
        </StyledBoxFlexStartSmallGap>

        {/* BOTTOM ROW - ADDRESS DETAILS */}
        {showAddress && address && <AddressSection address={address} />}
      </Box>
    </Tooltip>
  );
});

const Container = styled(Box)`
  z-index: 20;
  position: relative;
  width: ${CARD_WIDTH_FULL_WIDTH}px;
`;

interface DraggableCardProps extends ConditionalDraggableCardProps {
  allowListeners?: boolean;
  dragActionActive: boolean;
  selected: boolean;
  hovered?: boolean;
  setHovered: (hovered: boolean) => void;
  isUnassignment: boolean | undefined;
  isAssignEmployment: boolean | undefined;
  showPopoverDialog: boolean;
  setShouldShowPopoverDialog: (payload: {
    id: string;
    scheduleStart?: string | undefined;
    isUnassignment?: boolean | undefined;
    isRouteUnassignment?: boolean | undefined;
  }) => void;
}

export function DraggableCard(props: DraggableCardProps) {
  const {
    id,
    allowListeners = true,
    onHandleSelect,
    scheduleStart,
    scheduleId,
    planningEntityId,
    planningEntityType,
    dragActionActive,
    selected,
    isUnassignment,
    isAssignEmployment,
    showPopoverDialog,
    setShouldShowPopoverDialog
  } = props;
  const [disabled, setDisabled] = useState(false);

  const { isLoading, onClose, onHandleAppointmentConfirmation } = useContext(
    PopOverConfirmationContext
  );

  const [, setAssignEmploymentEditAppointmentType] = useRecoilState<
    EditAppointmentType | null | undefined
  >(assignEmploymentEditAppointmentTypeAtom);
  const [, setSelectedContextMenuOption] = useRecoilState(
    selectedContextMenuOptionAtom
  );

  const isToPlan = planningEntityType === 'ORGANIZATIONAL_UNIT_TO_PLAN';

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    disabled,
    data: {
      planningEntityType,
      planningEntityId,
      scheduleStart,
      scheduleId,
      isToPlan
    }
  });

  const style = {
    cursor: 'grab',
    opacity: isDragging || (selected && dragActionActive) ? 0.5 : 1
  };

  function onSelectClick(e) {
    if (e.target.classList.contains('MuiBackdrop-root')) {
      return;
    }
    onHandleSelect?.(id);
  }

  return (
    <PopoverWarningDialog
      open={showPopoverDialog && !isLoading}
      onClose={() => {
        if (isAssignEmployment) {
          setShouldShowPopoverDialog({ id: '' });
          setSelectedContextMenuOption(null);
          return setAssignEmploymentEditAppointmentType(null);
        }

        onClose?.();
        setShouldShowPopoverDialog({ id: '' });
      }}
      onClickAppointment={(editAppointmentType: EditAppointmentType) => {
        if (isAssignEmployment) {
          setShouldShowPopoverDialog({ id: '' });
          return setAssignEmploymentEditAppointmentType(editAppointmentType);
        }

        onHandleAppointmentConfirmation?.(editAppointmentType, isUnassignment);
        setShouldShowPopoverDialog({ id: '' });
      }}
    >
      <Container
        className="draggable-card"
        ref={setNodeRef}
        onClick={onSelectClick}
        sx={{
          ...style
        }}
      >
        <ActivityCard
          component="div"
          setDragDisabled={setDisabled}
          listeners={allowListeners ? listeners : undefined}
          attributes={attributes}
          {...props}
        />
      </Container>
    </PopoverWarningDialog>
  );
}

interface ConditionalDraggableCardProps extends CardProps {
  component?: ElementType<any>;
  planningEntityType: PlanningEntityType;
  scheduleStart: string;
  scheduleId?: string;
  isAlwaysActivityCard?: boolean;
  selected?: boolean;
  onHandleSelect?: (
    id: string,
    planningEntityType?: PlanningEntityType,
    employeeId?: string
  ) => void;
  onChangeMousePosition?: (pos: { x: number; y: number }) => void;
  readOnly?: boolean;
  isToPlan?: boolean;
}

export function ConditionalDraggableCard(props: ConditionalDraggableCardProps) {
  const {
    component,
    id,
    isAlwaysActivityCard,
    isRecurring,
    selected,
    scheduleStart,
    onChangeMousePosition,
    isToPlan
  } = props;
  const [hovered, setHovered] = useState<boolean>(false);
  const dragActionActive = useRecoilValue<boolean>(dragActionActiveAtom);

  const [
    {
      id: popoverOccurenceId,
      isUnassignment,
      isRouteUnassignment,
      scheduleStart: popoverScheduleStart,
      isAssignEmployment,
      isDragAction
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const hasScheduleStart = !!(popoverScheduleStart && scheduleStart);

  // We compare only the YYYY-MM-DD of the dates, not the time.
  const popoverScheduleStartDate: string = dayjs(popoverScheduleStart).isValid()
    ? dayjs(popoverScheduleStart).format('YYYY-MM-DD')
    : '';
  const currentScheduleStartDate: string = dayjs(scheduleStart).isValid()
    ? dayjs(scheduleStart).format('YYYY-MM-DD')
    : '';

  const isPopOverId = popoverOccurenceId === id;
  const showPopoverDialog: boolean =
    isRecurring &&
    (isRouteUnassignment ||
      (isPopOverId && !isDragAction) ||
      popoverOccurenceId === 'IS_MULTIPLE' ||
      (isPopOverId &&
        !!isAssignEmployment &&
        hasScheduleStart &&
        popoverScheduleStartDate === currentScheduleStartDate));

  // Sometimes while dragging other cards gets and stays hovered making it a DraggableCard until you hover out of it again, so we need to reset the hovered state
  useEffect(() => {
    if (!dragActionActive && !selected) {
      setHovered(false);
    }
  }, [dragActionActive, selected]);

  return (
    <Box
      component={component || 'li'}
      onMouseDown={(e: MouseEvent) =>
        onChangeMousePosition?.({ x: e.clientX, y: e.clientY })
      }
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        !dragActionActive && !selected && setHovered(false);
      }}
      sx={{
        '+ * .activity-card': {
          marginTop: '8px'
        }
      }}
    >
      <DraggableCard
        {...props}
        dragActionActive={dragActionActive}
        hovered={hovered}
        isToPlan={isToPlan}
        setHovered={setHovered}
        selected={!!selected}
        isUnassignment={isUnassignment}
        isAssignEmployment={isAssignEmployment}
        setShouldShowPopoverDialog={setShouldShowPopoverDialog}
        showPopoverDialog={showPopoverDialog}
        allowListeners={
          !isAlwaysActivityCard &&
          !props.isAbsent &&
          !props.readOnly &&
          (hovered || popoverOccurenceId === id)
        }
      />
    </Box>
  );
}
